import {
  Link,
  Outlet,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useStream } from "../contexts/StreamContext";
import { usePageData } from "../lib/api";
import { type DirectoryData, directoryPath } from "../lib/publicPages";
import { type StationStatus, useStationStatus } from "../lib/stationStatus";
import PageShell from "./PageShell";
import PageStatus from "./PageStatus";

/**
 * The frame every station page shares: who the station is, and the tabs
 * right under the name. The live player is not part of the frame — it lives
 * in the About tab's body, and the site-wide dock carries playback on every
 * other page (see PlayerDock). The audio element itself belongs to the
 * stream context, so the music keeps going as you move around — which is
 * also why everything in here navigates with <Link> rather than plain
 * anchors.
 */

interface StationPageContextValue {
  stationSlug: string;
  status: StationStatus;
  /** The station's shows and DJs, loaded once for both list tabs. */
  directory: DirectoryData | null;
}

const StationPageContext = createContext<StationPageContextValue | null>(null);

export const useStationPage = (): StationPageContextValue => {
  const context = useContext(StationPageContext);
  if (!context) {
    throw new Error("useStationPage must be used inside a station route");
  }
  return context;
};

/**
 * True once the page has scrolled far enough that the header has reached the
 * top. Watches a sentinel just above it rather than the header itself, which
 * a `position: sticky` element can't report about itself.
 *
 * A callback ref, not a mount effect: the header only renders once the
 * station has loaded, so at mount there is no sentinel to observe yet and a
 * one-shot effect would attach to nothing and never look again.
 */
const useStuck = () => {
  const [stuck, setStuck] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const sentinelRef = useCallback((sentinel: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!sentinel) {
      setStuck(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) =>
      setStuck(!entry.isIntersecting),
    );
    observer.observe(sentinel);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { sentinelRef, stuck };
};

/**
 * The tabs, each owning the routes that belong under it — a show's own
 * page keeps "Shows" lit, which `activeProps` alone can't do since its URL
 * doesn't sit below the tab's.
 */
const TABS = [
  {
    to: "/$stationSlug",
    label: "About",
    routes: ["/$stationSlug/"],
  },
  {
    to: "/$stationSlug/recent",
    label: "Recently played",
    routes: ["/$stationSlug/recent"],
  },
  {
    to: "/$stationSlug/shows",
    label: "Shows",
    routes: [
      "/$stationSlug/shows",
      "/$stationSlug/shows/$showSlug",
      "/$stationSlug/shows/$showSlug/episodes",
      "/$stationSlug/shows/$showSlug/episodes/$episodeId",
    ],
  },
  {
    to: "/$stationSlug/djs",
    label: "DJs",
    routes: ["/$stationSlug/djs", "/$stationSlug/djs/$djSlug"],
  },
] as const;

const StationLayout = () => {
  const { stationSlug } = useParams({ from: "/$stationSlug" });
  const state = useStationStatus(stationSlug);
  const directory = usePageData<DirectoryData>(directoryPath(stationSlug));
  const { setSource, setNowPlaying } = useStream();
  const { sentinelRef, stuck } = useStuck();
  const leafRoute = useRouterState({
    select: (state) => state.matches.at(-1)?.routeId ?? "",
  });

  const status = state.kind === "ready" ? state.data : null;

  // Hand the stream to the site-wide player, and keep its readout current.
  useEffect(() => {
    if (!status) {
      return;
    }
    setSource({
      id: status.id,
      name: status.name,
      streamUrl: status.stream_url,
      logoUrl: status.logo_url || undefined,
      href: `/${stationSlug}`,
    });
    setNowPlaying({
      show: status.current_show?.name,
      track: status.current_track?.name,
      artist: status.current_track?.artist?.name,
    });
  }, [status, stationSlug, setSource, setNowPlaying]);

  useEffect(() => {
    if (status) {
      document.title = `${status.name} · StreamLover`;
    }
    return () => {
      document.title = "StreamLover — We love radio. It deserves better.";
    };
  }, [status]);

  if (!status) {
    return (
      <PageStatus
        state={state}
        eyebrow={`streamlover.app/${stationSlug}`}
        missingTitle="No station here."
        missingBody="This address doesn't match a station on StreamLover — check the link, or find the station in the app."
      />
    );
  }

  return (
    <PageShell>
      {/* The header stays logo and name, so the tabs sit right under the
          station's identity; the StreamLover pitch closes the page instead. */}
      <header className="station station--head">
        {status.logo_url && (
          <img className="station__logo" src={status.logo_url} alt="" />
        )}
        <h1 className="station__name">{status.name}</h1>
      </header>

      <div ref={sentinelRef} className="stickyhead__sentinel" aria-hidden />
      <div className={`stickyhead${stuck ? " is-stuck" : ""}`}>
        <div className="stickyhead__tabs">
          <nav
            className="stationtabs stickyhead__inner"
            aria-label="Station sections"
          >
            {TABS.map((tab) => {
              const active = (tab.routes as readonly string[]).includes(
                leafRoute,
              );
              return (
                <Link
                  key={tab.to}
                  to={tab.to}
                  params={{ stationSlug }}
                  className={`pill${active ? " is-active" : ""}`}
                  aria-current={active ? "page" : undefined}
                >
                  {tab.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="station">
        <StationPageContext.Provider
          value={{
            stationSlug,
            status,
            directory: directory.kind === "ready" ? directory.data : null,
          }}
        >
          <Outlet />
        </StationPageContext.Provider>
      </main>

      {/* The StreamLover pitch closes every station page as a full-width
          inverted band — the site's voice on its own ground, so it never
          competes with the station's content in the column above. */}
      <section className="stationcloser">
        <div className="stationcloser__inner">
          <span className="stationcloser__divider" aria-hidden>
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
            <i />
          </span>
          <h2 className="stationcloser__title">It's better in the app</h2>
          <p className="stationcloser__pitch">
            {status.name} is on <a href="/">StreamLover</a>, the streaming radio
            hub for people who love music.
          </p>
          <p className="stationcloser__pitch">
            Listen on the go, play past episodes, get reminder notifications,
            and more.
          </p>
          <a className="btn station__app" href="/#get">
            Get the app
          </a>
        </div>
      </section>
    </PageShell>
  );
};

export default StationLayout;
